/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { BEM, useToggle } from "@adapptio/lib-components";

import "./FieldLabel.scss";
import {
	FieldFloatingPanel,
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE
} from "../FieldFloatingPanel";
import { useForkRef } from "../../../hooks";
import { ClickAwayListener, FC_ANCHOR, FC_DIRECTION, FC_SIZE } from "../../core";

export interface IFieldLabelProps {
	children?: React.ReactNode;
	className?: string;
	hint?: React.ReactNode;
	hintOpen?: boolean;
	onHintOpen?: () => void;
	onHintClose?: () => void;
	forId?: string;
	grow?: boolean;
	title?: string;
	preventTruncate?: boolean;
}

const FieldLabel = React.forwardRef<HTMLLabelElement, IFieldLabelProps>(
	(
		{ children, className, forId, grow, title, preventTruncate, hint, hintOpen, onHintOpen, onHintClose },
		ref
	) => {
		const labelRef = useRef();
		const forkRef = useForkRef(labelRef, ref);

		const baseClass = BEM("edt-field-label", null, {
			"with-hint": !!hint,
			"hint-open": !!hintOpen,
			grow,
			truncate: !preventTruncate
		});

		if (className) {
			baseClass.add(className);
		}

		const handleLabelClick = useCallback(() => {
			if (hint) {
				onHintOpen();
			}
		}, [ hint ]);

		return (
			<>
				<label
					ref={forkRef}
					title={title}
					htmlFor={forId}
					className={baseClass.toClassName()}
					onClick={handleLabelClick}
				>
					<span className={baseClass.element("text").toClassName()}>{children}</span>
				</label>
				<ClickAwayListener onClickAway={onHintClose}>
					<FieldFloatingPanel
						for={labelRef}
						isOpened={hintOpen}
						anchor={FC_ANCHOR.LEFT_CENTER}
						offset={40}
						direction={FC_DIRECTION.HR}
						size={FC_SIZE.AUTO}
						style={FIELD_FLOATING_PANEL_STYLE.STICKY}
						schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
						onClose={onHintClose}
					>
						{hint}
					</FieldFloatingPanel>
				</ClickAwayListener>
			</>
		);
	}
);

const FieldLabelMemo = React.memo(FieldLabel);

export { FieldLabelMemo as FieldLabel };
